"use client";

import { cn } from "utils/utils";
import { formatsForGame, formatLabel } from "utils/constants";
import { useGamesForCapability } from "modules/common/gameRegistryContext";
import type { GameId } from "../../model/models";

const colorPalette = {
  Banned: "bg-card-legalities-banned",
  Legal: "bg-card-legalities-legal",
  Restricted: "bg-card-legalities-restricted",
  NotLegal: "bg-card-legalities-not-legal",
};

// Spelled out because uppercasing the raw value renders "NOTLEGAL".
const statusLabels: Record<string, string> = {
  Legal: "LEGAL",
  Banned: "BANNED",
  Restricted: "RESTRICTED",
  NotLegal: "NOT LEGAL",
};

const FormatLegalityChip = ({
  format,
  legality,
  bgColorClass,
  game,
}: {
  format: string;
  legality: string;
  bgColorClass: string;
  game: GameId;
}) => {
  return (
    <div className="w-full p-1 sm:w-1/2" role="listitem">
      <div className="grid grid-cols-2 content-center items-center gap-1 sm:grid-cols-3">
        <div
          className={cn(
            `rounded-selector col-span-1 flex content-stretch items-center justify-center py-0.5 shadow-sm sm:col-span-2`,
            bgColorClass
          )}
          aria-label={`${formatLabel(game, format)} format status: ${legality}`}
        >
          <span className="text-primary-content text-xs">{legality}</span>
        </div>
        <div className="text-primary">{formatLabel(game, format)}</div>
      </div>
    </div>
  );
};

export const CardDetailsLegalities = ({
  legalities,
  game,
  isDialog = false,
}: {
  legalities?: Record<string, string> | null;
  game: GameId;
  isDialog?: boolean;
}) => {
  const { games } = useGamesForCapability("legalities");
  if (!games.includes(game)) {
    return null;
  }

  const formats = [
    ...new Set([...formatsForGame(game), ...Object.keys(legalities ?? {})]),
  ];
  if (formats.length === 0) {
    return null;
  }

  return (
    <section aria-labelledby="legalities-heading">
      {isDialog && <div className="divider" />}
      <header>
        <h2 className="mb-1 mt-1 text-lg" id="legalities-heading">
          Legalities
        </h2>
      </header>
      <div
        className="grid grid-cols-1 gap-1 sm:grid-cols-2"
        role="list"
        aria-label="Format legalities"
      >
        {formats.map((format) => {
          const status = legalities?.[format] ?? "";
          const legalityStatus = statusLabels[status] ?? "UNKNOWN";

          const bgColorClass =
            colorPalette[status as keyof typeof colorPalette] ??
            colorPalette.NotLegal;

          return (
            <FormatLegalityChip
              key={format}
              format={format}
              legality={legalityStatus}
              bgColorClass={bgColorClass}
              game={game}
            />
          );
        })}
      </div>
    </section>
  );
};
